import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Download, 
  Share2, 
  Info, 
  Calculator,
  DollarSign,
  Calendar,
  TrendingUp,
  BarChart3,
  PieChart,
  FileText
} from 'lucide-react';
// import { Line, Doughnut } from 'react-chartjs-2';
// import {
//   Chart as ChartJS,
//   CategoryScale,
//   LinearScale,
//   PointElement,
//   LineElement,
//   Title,
//   Tooltip,
//   Legend,
//   ArcElement,
// } from 'chart.js';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useScrollToTopOnMount } from '../hooks/useScrollToTop';

// ChartJS.register(
//   CategoryScale,
//   LinearScale,
//   PointElement,
//   LineElement,
//   Title,
//   Tooltip,
//   Legend,
//   ArcElement
// );

interface EMICalculation {
  monthlyEMI: number;
  totalInterest: number;
  totalAmount: number;
  totalProcessingFee: number;
  repaymentSchedule: Array<{
    month: number;
    emi: number;
    principal: number;
    interest: number;
    remainingBalance: number;
  }>;
  chartData: {
    labels: string[];
    datasets: any[];
  };
  pieData: {
    labels: string[];
    datasets: any[];
  };
}

const CreditCardEMICalculator = () => {
  const [inputs, setInputs] = useState({
    purchaseAmount: 50000,
    tenure: 12,
    interestRate: 18,
    processingFee: 0,
    processingFeeType: 'fixed', // 'fixed' or 'percentage'
    startDate: new Date().toISOString().split('T')[0]
  });

  const [result, setResult] = useState<EMICalculation | null>(null);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [showNoInterestComparison, setShowNoInterestComparison] = useState(false);

  // Scroll to top when component mounts
  useScrollToTopOnMount();

  // Update document title and meta description
  useEffect(() => {
    document.title = 'Credit Card EMI Calculator – Calculate Monthly Installments | Simran Industries';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Use our free credit card EMI calculator to calculate monthly installments, interest, and total repayment amount. Plan your credit card purchases with ease.');
    }

    // Add schema markup
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Credit Card EMI Calculator",
      "description": "Free online credit card EMI calculator to calculate monthly installments and interest",
      "url": "https://simranindustries.com/calculators/credit-card-emi-calculator",
      "applicationCategory": "FinanceApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "INR"
      },
      "mainEntity": {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How is EMI calculated?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "EMI is calculated using the reducing balance method: EMI = [P × R × (1+R)^N] / [(1+R)^N – 1] where P is loan amount, R is monthly interest rate, and N is number of months."
            }
          },
          {
            "@type": "Question",
            "name": "What is the reducing balance method?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "In the reducing balance method, interest is calculated on the remaining principal amount after each EMI payment. This results in decreasing interest payments over time."
            }
          },
          {
            "@type": "Question",
            "name": "How does processing fee affect EMI?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Processing fee is added to the principal amount before calculating EMI. This increases the total amount to be repaid and slightly increases the monthly EMI."
            }
          }
        ]
      }
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  const calculateEMI = () => {
    const {
      purchaseAmount,
      tenure,
      interestRate,
      processingFee,
      processingFeeType
    } = inputs;

    // Calculate processing fee
    const actualProcessingFee = processingFeeType === 'percentage' 
      ? (purchaseAmount * processingFee / 100) 
      : processingFee;

    // Principal amount including processing fee
    const principal = purchaseAmount + actualProcessingFee;
    
    // Monthly interest rate
    const monthlyRate = interestRate / 100 / 12;
    
    // Calculate EMI using the formula
    const emi = principal * monthlyRate * Math.pow(1 + monthlyRate, tenure) / 
                (Math.pow(1 + monthlyRate, tenure) - 1);

    // Generate repayment schedule
    const schedule = [];
    let remainingBalance = principal;
    
    for (let month = 1; month <= tenure; month++) {
      const interest = remainingBalance * monthlyRate;
      const principalPaid = emi - interest;
      remainingBalance = remainingBalance - principalPaid;
      
      schedule.push({
        month,
        emi: Math.round(emi),
        principal: Math.round(principalPaid),
        interest: Math.round(interest),
        remainingBalance: Math.max(0, Math.round(remainingBalance))
      });
    }

    // Calculate totals
    const totalAmount = emi * tenure;
    const totalInterest = totalAmount - principal;

    // Chart data for interest vs principal over time
    const chartLabels = schedule.map(item => `Month ${item.month}`);
    const principalData = schedule.map(item => item.principal);
    const interestData = schedule.map(item => item.interest);

    const chartData = {
      labels: chartLabels,
      datasets: [
        {
          label: 'Principal',
          data: principalData,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
        },
        {
          label: 'Interest',
          data: interestData,
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.4,
        },
      ],
    };

    // Pie chart data for total breakdown
    const pieData = {
      labels: ['Principal', 'Interest', 'Processing Fee'],
      datasets: [
        {
          data: [purchaseAmount, totalInterest, actualProcessingFee],
          backgroundColor: [
            'rgba(59, 130, 246, 0.8)',
            'rgba(239, 68, 68, 0.8)',
            'rgba(245, 158, 11, 0.8)',
          ],
          borderColor: [
            'rgba(59, 130, 246, 1)',
            'rgba(239, 68, 68, 1)',
            'rgba(245, 158, 11, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };

    setResult({
      monthlyEMI: Math.round(emi),
      totalInterest: Math.round(totalInterest),
      totalAmount: Math.round(totalAmount),
      totalProcessingFee: Math.round(actualProcessingFee),
      repaymentSchedule: schedule,
      chartData,
      pieData
    });
  };

  useEffect(() => {
    calculateEMI();
  }, [inputs]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const downloadPDF = () => {
    if (!result) return;
    
    const content = `
Credit Card EMI Calculator Results

Purchase Amount: ${formatCurrency(inputs.purchaseAmount)}
Tenure: ${inputs.tenure} months
Interest Rate: ${inputs.interestRate}% per annum
Processing Fee: ${inputs.processingFeeType === 'percentage' ? inputs.processingFee + '%' : formatCurrency(inputs.processingFee)}

RESULTS:
Monthly EMI: ${formatCurrency(result.monthlyEMI)}
Total Interest: ${formatCurrency(result.totalInterest)}
Total Amount: ${formatCurrency(result.totalAmount)}
Processing Fee: ${formatCurrency(result.totalProcessingFee)}

Generated on: ${new Date().toLocaleDateString()}
    `;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'credit-card-emi-results.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const shareResults = () => {
    if (!result) return;
    
    const shareText = `My credit card EMI calculation:
- Purchase amount: ${formatCurrency(inputs.purchaseAmount)}
- Monthly EMI: ${formatCurrency(result.monthlyEMI)}
- Total interest: ${formatCurrency(result.totalInterest)}
- Tenure: ${inputs.tenure} months`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Credit Card EMI Results',
        text: shareText,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(shareText);
      alert('Results copied to clipboard!');
    }
  };

  const tooltips = {
    purchaseAmount: "The total amount you want to convert to EMI",
    tenure: "Number of months to repay the amount",
    interestRate: "Annual interest rate charged by the credit card",
    processingFee: "One-time fee charged for converting to EMI",
    processingFeeType: "Whether the fee is a fixed amount or percentage of purchase amount"
  };

  const tenureOptions = [3, 6, 9, 12, 18, 24];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <CreditCard className="h-8 w-8 text-amber-600 mr-3" />
              <h1 className="text-4xl font-bold text-gray-900">Credit Card EMI Calculator</h1>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Calculate your monthly EMI for credit card purchases. Understand interest, processing fees, 
              and total repayment amount with our comprehensive calculator.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Calculator className="h-6 w-6 text-amber-600 mr-2" />
                EMI Details
              </h2>

              <div className="space-y-6">
                {/* Purchase Amount */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purchase Amount (₹)
                    <button
                      onMouseEnter={() => setShowTooltip('purchaseAmount')}
                      onMouseLeave={() => setShowTooltip(null)}
                      className="ml-1 text-gray-400 hover:text-gray-600"
                    >
                      <Info size={14} />
                    </button>
                  </label>
                  <input
                    type="number"
                    value={inputs.purchaseAmount}
                    onChange={(e) => handleInputChange('purchaseAmount', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                    min="1000"
                    step="1000"
                  />
                  {showTooltip === 'purchaseAmount' && (
                    <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                      {tooltips.purchaseAmount}
                    </div>
                  )}
                </div>

                {/* Tenure */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tenure (Months)
                    <button
                      onMouseEnter={() => setShowTooltip('tenure')}
                      onMouseLeave={() => setShowTooltip(null)}
                      className="ml-1 text-gray-400 hover:text-gray-600"
                    >
                      <Info size={14} />
                    </button>
                  </label>
                  <select
                    value={inputs.tenure}
                    onChange={(e) => handleInputChange('tenure', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                  >
                    {tenureOptions.map(option => (
                      <option key={option} value={option}>{option} months</option>
                    ))}
                  </select>
                  {showTooltip === 'tenure' && (
                    <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                      {tooltips.tenure}
                    </div>
                  )}
                </div>

                {/* Interest Rate */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Interest Rate (% per annum)
                    <button
                      onMouseEnter={() => setShowTooltip('interestRate')}
                      onMouseLeave={() => setShowTooltip(null)}
                      className="ml-1 text-gray-400 hover:text-gray-600"
                    >
                      <Info size={14} />
                    </button>
                  </label>
                  <input
                    type="number"
                    value={inputs.interestRate}
                    onChange={(e) => handleInputChange('interestRate', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                    min="0"
                    max="50"
                    step="0.1"
                  />
                  {showTooltip === 'interestRate' && (
                    <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                      {tooltips.interestRate}
                    </div>
                  )}
                </div>

                {/* Processing Fee */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Processing Fee
                      <button
                        onMouseEnter={() => setShowTooltip('processingFee')}
                        onMouseLeave={() => setShowTooltip(null)}
                        className="ml-1 text-gray-400 hover:text-gray-600"
                      >
                        <Info size={14} />
                      </button>
                    </label>
                    <input
                      type="number"
                      value={inputs.processingFee}
                      onChange={(e) => handleInputChange('processingFee', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                      min="0"
                      step="1"
                    />
                    {showTooltip === 'processingFee' && (
                      <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                        {tooltips.processingFee}
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fee Type
                      <button
                        onMouseEnter={() => setShowTooltip('processingFeeType')}
                        onMouseLeave={() => setShowTooltip(null)}
                        className="ml-1 text-gray-400 hover:text-gray-600"
                      >
                        <Info size={14} />
                      </button>
                    </label>
                    <select
                      value={inputs.processingFeeType}
                      onChange={(e) => handleInputChange('processingFeeType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                    >
                      <option value="fixed">Fixed (₹)</option>
                      <option value="percentage">Percentage (%)</option>
                    </select>
                    {showTooltip === 'processingFeeType' && (
                      <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                        {tooltips.processingFeeType}
                      </div>
                    )}
                  </div>
                </div>

                {/* Start Date */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={inputs.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                {/* No Interest Comparison Toggle */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="noInterestComparison"
                    checked={showNoInterestComparison}
                    onChange={(e) => setShowNoInterestComparison(e.target.checked)}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <label htmlFor="noInterestComparison" className="text-sm text-gray-700">
                    Show comparison with no-interest EMI
                  </label>
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="space-y-6">
              {result && (
                <>
                  {/* Main Results */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <DollarSign className="h-6 w-6 text-green-600 mr-2" />
                      EMI Summary
                    </h2>

                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-green-800 mb-2">Monthly EMI</h3>
                        <p className="text-3xl font-bold text-green-600">{formatCurrency(result.monthlyEMI)}</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-blue-600 mb-1">Total Interest</h4>
                          <p className="text-lg font-semibold text-blue-800">{formatCurrency(result.totalInterest)}</p>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-purple-600 mb-1">Total Amount</h4>
                          <p className="text-lg font-semibold text-purple-800">{formatCurrency(result.totalAmount)}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-600 mb-1">Processing Fee</h4>
                          <p className="text-lg font-semibold text-gray-900">{formatCurrency(result.totalProcessingFee)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-600 mb-1">Tenure</h4>
                          <p className="text-lg font-semibold text-gray-900">{inputs.tenure} months</p>
                        </div>
                      </div>

                      {/* No Interest Comparison */}
                      {showNoInterestComparison && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-yellow-800 mb-2">No-Interest EMI Comparison</h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-600">No-Interest EMI:</p>
                              <p className="font-semibold">{formatCurrency(inputs.purchaseAmount / inputs.tenure)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Interest Saved:</p>
                              <p className="font-semibold text-green-600">{formatCurrency(result.totalInterest)}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                      <button
                        onClick={downloadPDF}
                        className="flex items-center justify-center px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
                      >
                        <Download size={16} className="mr-2" />
                        Download Results
                      </button>
                      <button
                        onClick={shareResults}
                        className="flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                      >
                        <Share2 size={16} className="mr-2" />
                        Share Results
                      </button>
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <BarChart3 className="h-5 w-5 text-amber-600 mr-2" />
                      Payment Breakdown
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Interest vs Principal Over Time</h4>
                        <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                          <p className="text-gray-500">Chart temporarily disabled</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Total Amount Breakdown</h4>
                        <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                          <p className="text-gray-500">Chart temporarily disabled</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Repayment Schedule */}
          {result && (
            <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 text-amber-600 mr-2" />
                Repayment Schedule
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Month
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        EMI
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Principal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Interest
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Remaining Balance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {result.repaymentSchedule.map((item) => (
                      <tr key={item.month} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.month}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(item.emi)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                          {formatCurrency(item.principal)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                          {formatCurrency(item.interest)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(item.remainingBalance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Information Section */}
          <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Info className="h-6 w-6 text-blue-600 mr-2" />
              How EMI Works
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">EMI Calculation</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Uses reducing balance method for accurate calculation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Formula: EMI = [P × R × (1+R)^N] / [(1+R)^N – 1]</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Where P = Principal, R = Monthly Rate, N = Tenure</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Processing fee is added to principal before calculation</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Important Notes</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Interest rates may vary based on credit card and bank</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Processing fees are typically 1-3% of purchase amount</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>EMI conversion is subject to bank approval</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Check with your bank for exact terms and conditions</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CreditCardEMICalculator; 